import React, { type JSX } from "react";
import type { TimezoneSetting } from "./useTimezoneSetting";
import { SectionHeader, SECTION_CLASS, SECTION_FOOTER_CLASS } from "./SettingsPrimitives";

const CLOCK_FORMAT: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
};

/** Scrollable list of the timezones matching the current search. */
function TimezoneList({ timezone }: { timezone: TimezoneSetting }): JSX.Element {
  return (
    <div className="block w-full h-48 overflow-y-auto rounded-md border border-gray-300 shadow-sm bg-white sm:text-sm">
      {timezone.filtered.map((tz) => (
        <button
          key={tz}
          type="button"
          onClick={() => timezone.select(tz)}
          className={`block w-full text-left px-3 py-1.5 text-sm ${
            tz === timezone.selected ? "bg-blue-600 text-white" : "text-gray-900 hover:bg-blue-50"
          }`}
        >
          {tz}
          {tz === timezone.browserTimezone ? " (browser default)" : ""}
        </button>
      ))}
      {timezone.filtered.length === 0 && (
        <p className="px-3 py-2 text-sm text-gray-400">No timezones match your search</p>
      )}
    </div>
  );
}

/** Timezone picker for every date shown in the admin console. */
export default function DisplaySettingsSection({
  timezone,
}: {
  timezone: TimezoneSetting;
}): JSX.Element {
  return (
    <div className={SECTION_CLASS}>
      <SectionHeader
        title="Display Settings"
        description="Configure how dates and times are displayed across the admin interface."
      />

      <div className="px-6 py-5 space-y-4">
        <div>
          <label htmlFor="timezone-select" className="block text-sm font-medium text-gray-700">
            Timezone
          </label>
          <p className="text-sm text-gray-500 mb-2">
            Your browser detected:{" "}
            <span className="font-mono text-gray-700">{timezone.browserTimezone}</span>
          </p>

          <input
            id="timezone-search"
            type="text"
            aria-label="Search timezones"
            value={timezone.search}
            onChange={(e) => timezone.setSearch(e.target.value)}
            placeholder="Search timezones (e.g. London, New_York, Tokyo)"
            className="mb-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />

          <TimezoneList timezone={timezone} />

          <p className="mt-2 text-xs text-gray-500">
            Current time in selected timezone:{" "}
            <span className="font-mono">
              {new Date().toLocaleString(undefined, {
                timeZone: timezone.selected,
                ...CLOCK_FORMAT,
              })}
            </span>
          </p>
        </div>
      </div>

      <div className={SECTION_FOOTER_CLASS}>
        <button
          type="button"
          onClick={timezone.resetToBrowser}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Reset to Browser Default
        </button>
      </div>
    </div>
  );
}
